import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getCurrentDiscount, getDiscountRules } from '../api/discountsApi'

export default function DiscountBanner({ projectId }) {
  const [isExpanded, setIsExpanded] = useState(false)

  const { data: currentDiscountData } = useQuery({
    queryKey: ['current-discount', projectId],
    queryFn: () => getCurrentDiscount(projectId),
  })

  const { data: rulesData } = useQuery({
    queryKey: ['discount-rules', projectId],
    queryFn: () => getDiscountRules(projectId),
    enabled: !!currentDiscountData?.data,
  })

  const currentDiscount = currentDiscountData?.data
  const rules = rulesData?.data

  if (!currentDiscount || !rules || !rules.is_active) {
    return null
  }

  const slotsRemaining = currentDiscount.slots_available

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-green-900">Discounts available for early buyers</h3>
            <p className="text-sm text-green-700">
              {slotsRemaining === 1
                ? 'Last discounted unit available'
                : `${slotsRemaining} discounted units remaining`
              }
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm text-green-700 hover:text-green-900 font-medium flex items-center gap-1 flex-shrink-0"
        >
          How it works
          <svg
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-green-200">
          <h4 className="font-medium text-green-900 mb-3">How discounts work on Beaver</h4>
          <div className="space-y-3 text-sm text-green-800">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p>
                <strong>Early buyers get the best deals.</strong> Developers offer genuine discounts to secure early presales. The discount percentage reduces with each reservation.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p>
                <strong>Benchmarked against registered valuations.</strong> Discounts are calculated from independent valuations, not inflated list prices.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p>
                <strong>See the actual discount on each unit.</strong> When you view a specific unit, you'll see exactly how much you can save.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p>
                <strong>Choose how to use it.</strong> Apply towards your deposit, reduce the purchase price, or a combination of both.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
